// AEO explainer — the "Customers stopped just Googling. Now they ask AI."
// education block. Originally built for the /getleads lander; the owner moved it
// to the main website (home page, after the pillars) so the lander stays lean
// and the main site carries the AI-search education.
import { ArrowRight, Bot } from "lucide-react";

const AEO_PLATFORM_LOGOS = {
  ChatGPT: "/chatgpt-logo.svg",
  Claude: "https://cdn.simpleicons.org/claude",
  Gemini: "https://cdn.simpleicons.org/googlegemini",
};

export function AeoExplainer({ onOpen, label = "Get my free Google ranking report" }) {
  return (
    <section className="px-5 py-20 sm:px-8 sm:py-24" data-testid="aeo-section">
      <div className="mx-auto max-w-3xl text-center lg:max-w-5xl">
        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-brand-indigo">The part your competitors haven&rsquo;t heard of</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
          Customers stopped just Googling. <span className="text-gradient">Now they ask AI.</span>
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-navy/55 lg:max-w-2xl lg:text-base">
          ChatGPT, Claude and Gemini don&rsquo;t show ten links — they name <b className="text-navy">one</b> business.
          If that&rsquo;s not you, you&rsquo;re invisible in the fastest-growing way people search.
        </p>
      </div>
      {/* the AI answer demo — one name, no page two */}
      <div className="mx-auto mt-8 max-w-2xl lg:mt-10">
        <div className="rounded-3xl border border-navy/8 bg-white p-5 shadow-soft-lg lg:p-7" data-testid="aeo-demo-card">
          <div className="flex justify-end">
            <p className="max-w-[85%] rounded-2xl rounded-br-md bg-brand-indigo px-4 py-2.5 text-[13.5px] font-semibold text-white">Who&rsquo;s the best dentist near me?</p>
          </div>
          <div className="mt-3 flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-navy"><Bot className="h-4 w-4 text-white" /></span>
            <div className="rounded-2xl rounded-tl-md bg-mist px-4 py-3 text-[13.5px] leading-relaxed text-navy/80">
              <p>I&rsquo;d recommend <b className="text-navy">Your Business</b> — 4.9&#9733; from 127 reviews, open now, and locals keep mentioning how easy it is to book.</p>
              <p className="mt-2 text-[12px] font-semibold text-navy/45">That&rsquo;s the whole answer. One name. No page two.</p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-center gap-5 border-t border-navy/8 pt-4">
            {["ChatGPT", "Claude", "Gemini"].map((p) => (
              <span key={p} className="flex items-center gap-1.5 text-[11px] font-bold text-navy/50">
                <img src={AEO_PLATFORM_LOGOS[p]} alt={p} className="h-4 w-4" /> {p}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* three quick truths */}
      <div className="mx-auto mt-8 grid max-w-4xl gap-4 sm:grid-cols-3 lg:max-w-5xl lg:gap-5">
        {[
          { t: "One name, not ten links", d: "AI doesn't hand over a list to scroll — it recommends a single business. First place is the only place." },
          { t: "It grows every single month", d: "More customers ask AI who to call every month — and they trust the answer like a friend's referral." },
          { t: "Your area is wide open", d: "Almost no local business is optimizing for AI search yet. The first one in your area to do it owns the answer." },
        ].map((c) => (
          <div key={c.t} className="rounded-2xl border border-navy/8 bg-white p-5 lg:p-6">
            <h3 className="font-display text-[15px] font-extrabold text-navy lg:text-base">{c.t}</h3>
            <p className="mt-1 text-[13px] leading-relaxed text-navy/55 lg:text-[14px]">{c.d}</p>
          </div>
        ))}
      </div>
      <p className="mx-auto mt-7 max-w-xl text-center text-[13.5px] font-bold leading-relaxed text-navy/70 lg:text-[15px]">
        Getting AI to say your name is called <span className="text-gradient">AEO — Answer Engine Optimization</span>. It&rsquo;s built into the Pro plan.
      </p>
      <div className="mt-5 text-center">
        <button type="button" data-testid="aeo-book-cta" onClick={onOpen}
          className="sl-glow-cta inline-flex items-center justify-center gap-2 rounded-full bg-brand-gradient px-7 py-3.5 font-display text-[15px] font-semibold text-white shadow-pill transition-all hover:-translate-y-0.5">
          {label} <ArrowRight className="h-4 w-4" />
        </button>
        <p className="mt-2.5 text-[12.5px] font-semibold text-navy/45" data-testid="aeo-deliverable-line">
          Free competitor report + website — built before your call, revealed on it, yours to keep either way.
        </p>
      </div>
    </section>
  );
}
